import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { clientAPI, uploadAPI } from '../../services/apiServices';
import { Button, Input, Select, Alert, Card, PageHeader, Spinner } from '../../components/common/UI';
import styles from './ClientPages.module.css';
import { formatDocumentInput, formatEmailInput, formatPhoneInput } from '../../utils/inputFormat';
import { requiredFieldMessage } from '../../utils/validationMessages';
import AuthenticatedImage from '../../components/common/AuthenticatedImage';

const EMPTY = {
  name: '',
  document: '',
  email: '',
  activityField: '',
  street: '',
  number: '',
  complement: '',
  neighborhood: '',
  city: '',
  state: '',
  zipCode: '',
  website: '',
  phone: '',
  whatsapp: '',
  clientType: 'business',
  avatar: '',
};

const ClientFormPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const isEditing = Boolean(id);
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  const typeOptions = useMemo(() => [
    { value: 'business', label: t('contactTypes.business') },
    { value: 'involved', label: t('contactTypes.involved') },
    { value: 'both', label: t('contactTypes.both') },
  ], [t]);

  useEffect(() => {
    if (!isEditing) return;
    clientAPI.getOne(id)
      .then(({ data }) => {
        const client = data.data;
        setForm({
          name: client.name || '',
          document: client.document || '',
          email: client.email || '',
          activityField: client.activityField || '',
          street: client.street || '',
          number: client.number || '',
          complement: client.complement || '',
          neighborhood: client.neighborhood || '',
          city: client.city || '',
          state: client.state || '',
          zipCode: client.zipCode || '',
          website: client.website || '',
          phone: client.phone || '',
          whatsapp: client.whatsapp || '',
          clientType: client.clientType || 'business',
          avatar: client.avatar || '',
        });
      })
      .catch(() => setError(t('clientForm.notFound')))
      .finally(() => setLoading(false));
  }, [id, isEditing, t]);

  const f = (key, formatter = (value) => value) => (event) => {
    setForm({ ...form, [key]: formatter(event.target.value) });
    setFieldErrors((current) => ({ ...current, [key]: '' }));
  };

  const handleAvatarChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError(t('clientForm.invalidImage'));
      return;
    }

    setSaving(true);
    setError('');
    try {
      const { data } = await uploadAPI.image(file);
      setForm((current) => ({ ...current, avatar: data.data.url }));
    } catch (err) {
      setError(err.response?.data?.error || t('clientForm.uploadError'));
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.name.trim()) {
      const message = requiredFieldMessage(t, t('clientForm.nameLabel'));
      setFieldErrors({ name: message });
      setError(message);
      return;
    }

    setSaving(true);
    setError('');
    try {
      if (isEditing) {
        await clientAPI.update(id, form);
      } else {
        await clientAPI.create(form);
      }
      navigate('/clients');
    } catch (err) {
      setError(err.response?.data?.error || t('clientForm.saveError'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Spinner size="lg" />;

  return (
    <div>
      <PageHeader
        title={isEditing ? t('clientForm.editTitle') : t('clientForm.newTitle')}
        subtitle={t('clientForm.subtitle')}
      />

      <Card>
        {error && <Alert variant="error" onClose={() => setError('')} style={{ marginBottom: '1rem' }}>{error}</Alert>}

        <form onSubmit={handleSubmit} noValidate>
          <div className={styles.formSection}>
            <h3 className={styles.formSectionTitle}>{t('clientForm.generalData')}</h3>
            <div className="mb-4 flex flex-wrap items-center gap-4">
              <label className="flex h-20 w-20 cursor-pointer items-center justify-center overflow-hidden rounded-full bg-sky-50 text-sm font-bold text-sky-500 ring-1 ring-sky-100 transition hover:ring-sky-300" aria-label={t('clientForm.uploadPhoto')}>
                {form.avatar ? (
                  <AuthenticatedImage source={form.avatar} alt="" className="h-full w-full object-cover" />
                ) : (
                  t('clientForm.photo')
                )}
                <input type="file" accept="image/*" className="sr-only" onChange={handleAvatarChange} />
              </label>
              <label className="inline-flex h-10 cursor-pointer items-center rounded-lg border border-slate-200 bg-white px-4 text-sm font-bold text-slate-800 shadow-sm transition hover:border-sky-200 hover:text-sky-600">
                {t('clientForm.uploadPhoto')}
                <input type="file" accept="image/*" className="sr-only" onChange={handleAvatarChange} />
              </label>
            </div>
            <div className={styles.formGrid}>
              <Input label={t('clientForm.nameLabel')} value={form.name} onChange={f('name')} placeholder={t('clientForm.namePlaceholder')} error={fieldErrors.name} required />
              <Input label={t('clientForm.documentLabel')} value={form.document} onChange={f('document', formatDocumentInput)} placeholder={t('clientForm.documentPlaceholder')} helpText={t('clientForm.documentHelp')} />
              <Input label={t('auth.email')} type="email" value={form.email} onChange={f('email', formatEmailInput)} placeholder="contact@email.com" />
              <Input label={t('clientForm.activityFieldLabel')} value={form.activityField} onChange={f('activityField')} placeholder={t('clientForm.activityFieldPlaceholder')} />
              <Select label={t('clientForm.contactTypeLabel')} value={form.clientType} onChange={f('clientType')} options={typeOptions} />
            </div>
          </div>

          <div className={styles.formSection}>
            <h3 className={styles.formSectionTitle}>{t('contacts.address')}</h3>
            <div className={styles.formGrid}>
              <Input label={t('clientForm.zipCode')} value={form.zipCode} onChange={f('zipCode')} placeholder="00000-000" />
              <Input label={t('clientForm.street')} value={form.street} onChange={f('street')} placeholder={t('clientForm.streetPlaceholder')} />
              <Input label={t('clientForm.number')} value={form.number} onChange={f('number')} placeholder="123" />
              <Input label={t('clientForm.complement')} value={form.complement} onChange={f('complement')} placeholder={t('clientForm.complementPlaceholder')} />
              <Input label={t('clientForm.neighborhood')} value={form.neighborhood} onChange={f('neighborhood')} />
              <Input label={t('clientForm.city')} value={form.city} onChange={f('city')} />
              <Input label={t('clientForm.state')} value={form.state} onChange={f('state')} placeholder="PR" maxLength={2} />
            </div>
          </div>

          <div className={styles.formSection}>
            <h3 className={styles.formSectionTitle}>{t('contacts.contactInfo')}</h3>
            <div className={styles.formGrid}>
              <Input label={t('profile.phone')} value={form.phone} onChange={f('phone', formatPhoneInput)} placeholder="(00) 0000-0000" />
              <Input label="WhatsApp" value={form.whatsapp} onChange={f('whatsapp', formatPhoneInput)} placeholder="(00) 00000-0000" />
              <Input label={t('contacts.website')} value={form.website} onChange={f('website')} placeholder="empresa.com" />
            </div>
          </div>

          <div className={styles.formActions}>
            <Button type="submit" loading={saving}>
              {isEditing ? t('profile.saveChanges') : t('contacts.create')}
            </Button>
            <Link to="/clients">
              <Button type="button" variant="secondary">{t('common.cancel')}</Button>
            </Link>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default ClientFormPage;
